import { Icon } from "@iconify/react";
import {
  Badge,
  Button,
  Card,
  Col,
  Flex,
  Form,
  Grid,
  Input,
  message,
  Pagination,
  Popover,
  Row,
  Select,
  Table,
  Tag,
  Typography,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import useTitleStore from "../../store/titleStore";
import { useTableHeight } from "../../hooks/useTableHeight";
import useDebounce from "../../hooks/useDebounce";
import UpdatedByCell from "../../components/UpdatedByCell";
import StatusTag from "../../components/StatusTag";
import { institutionalVerificationService } from "../../services/institutional-verification.service";
import { institutionalTypeService } from "../../services/institutionalType.service";
import { sectorService } from "../../services/sector.service";
import { positionService } from "../../services/position.service";
import { dashboardService } from "../../services/dashboard.service";
import pallete from "../../utils/pallete";
import { VERIFICATION_STATUS_OPTIONS } from "./constants";

export default function InstitutionalVerificationIndex() {
  const breakpoint = Grid.useBreakpoint();

  const navigate = useNavigate();
  const { setTitle } = useTitleStore();
  const tableHeight = useTableHeight();

  const [messageApi, contextHolder] = message.useMessage();
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [provinces, setProvinces] = useState([]);
  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    keyword: null,
  });
  const [filters, setFilters] = useState({
    type: null,
    institutionForm: null,
    province: null,
    sectoral: null,
    position: null,
    legalityStatus: null,
    facilityStatus: null,
    programStatus: null,
  });
  const [exportLoading, setExportLoading] = useState(false);

  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      const response = await institutionalVerificationService.exportExcel({
        keyword: dataParams.keyword || undefined,
        type: filters.type || undefined,
        institutionForm: filters.institutionForm || undefined,
        province: filters.province || undefined,
        sectoral: filters.sectoral || undefined,
        position: filters.position || undefined,
        legalityStatus: filters.legalityStatus || undefined,
        facilityStatus: filters.facilityStatus || undefined,
        programStatus: filters.programStatus || undefined,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const fileName =
        response.headers["content-disposition"]
          ?.split("filename=")?.[1]
          ?.replace(/"/g, "") || "lembaga-vokasi.xlsx";
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      messageApi.success("File berhasil diunduh");
    } catch (error) {
      console.error("Export excel error:", error);
      messageApi.error("Gagal mengunduh file Excel");
    } finally {
      setExportLoading(false);
    }
  };

  const { data: institutionTypes } = useQuery({
    queryKey: ["institution-type"],
    queryFn: () => institutionalTypeService.fetchAllWithoutPaginate(),
  });

  const { data: sectorals } = useQuery({
    queryKey: ["sectorals"],
    queryFn: () => sectorService.fetchAllWithoutPaginate(),
  });

  const { data: positions } = useQuery({
    queryKey: ["positions"],
    queryFn: () => positionService.fetchAllWithoutPaginate(),
  });

  const sectoralOptions = useMemo(() => {
    const list = Array.isArray(sectorals?.data)
      ? sectorals.data
      : Array.isArray(sectorals)
        ? sectorals
        : [];
    return list.map((item) => ({
      label: item.name,
      value: item.uuid,
    }));
  }, [sectorals]);

  const positionOptions = useMemo(() => {
    const list = Array.isArray(positions?.data)
      ? positions.data
      : Array.isArray(positions)
        ? positions
        : [];
    return list.map((item) => ({
      label: item.name,
      value: item.uuid,
    }));
  }, [positions]);

  const {
    data: instituteData,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ["institutional-verification", dataParams],
    queryFn: () => institutionalVerificationService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
  });

  const { data: dashboardData } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => dashboardService.fetchData(),
    placeholderData: keepPreviousData,
  });

  const metricsData = useMemo(() => {
    return [
      {
        key: "pending",
        title: "Lembaga yang Belum Terverifikasi",
        count: dashboardData?.data?.count?.pending || 0,
        icon: (
          <Icon icon="solar:clock-circle-bold-duotone" width={32} height={32} />
        ),
        backgroundColor: "#dba63d",
        description: "Lembaga dengan data legalitas yang belum diverifikasi.",
        isPrimary: true,
      },
      {
        key: "total",
        title: "Lembaga yang Terdaftar",
        count: dashboardData?.data?.count?.total || 0,
        icon: (
          <Icon icon="solar:document-bold-duotone" width={32} height={32} />
        ),
        backgroundColor: pallete.background.white,
        description: "Lembaga dengan data legalitas yang telah terverifikasi.",
        isPrimary: false,
      },
      {
        key: "rejected",
        title: "Lembaga yang Ditolak",
        count: dashboardData?.data?.count?.rejected || 0,
        icon: (
          <Icon
            icon="solar:notification-lines-remove-bold-duotone"
            width={32}
            height={32}
          />
        ),
        backgroundColor: pallete.background.white,
        description:
          "Lembaga dengan data legalitas yang tidak memenuhi persyaratan verifikasi.",
        isPrimary: false,
      },
      {
        key: "certified",
        title: "Lembaga yang Terverifikasi",
        count: dashboardData?.data?.count?.certified || 0,
        icon: (
          <Icon
            icon="solar:verified-check-bold-duotone"
            width={32}
            height={32}
          />
        ),
        backgroundColor: pallete.background.white,
        description:
          "Lembaga dengan data legalitas, fasilitas, dan program yang telah terverifikasi.",
        isPrimary: false,
      },
    ];
  }, [dashboardData]);

  useEffect(() => {
    setTitle("Verifikasi Lembaga");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          "https://ibnux.github.io/data-indonesia/provinsi.json",
        );
        setProvinces(await res.json());
      } catch {
        message.error("Gagal memuat data provinsi");
      }
    })();
  }, []);

  useEffect(() => {
    setDataParams((prev) => ({
      ...prev,
      ...filters,
      keyword: debouncedKeyword,
      page: 1,
    }));
  }, [debouncedKeyword, filters]);

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page,
    });
  };

  const columns = [
    {
      title: "No",
      align: "center",
      dataIndex: "id",
      width: 50,
      fixed: breakpoint.md && "left",
      render: (text, record, index) =>
        (instituteData?.pagination.page - 1) * dataParams.limit + index + 1,
    },
    {
      title: "ID & Nama Lembaga",
      dataIndex: "institutionId",
      width: 250,
      fixed: breakpoint.md && "left",
      render: (_, record) => (
        <>
          <Typography.Text strong>{record.name ?? "-"}</Typography.Text>
          <br />
          <Typography.Text
            type="secondary"
            style={{ fontSize: 13, fontStyle: "italic" }}
          >
            {record.institutionId ?? "-"}
          </Typography.Text>
        </>
      ),
    },
    {
      title: "Jenis & Status Lembaga",
      dataIndex: "type",
      width: 180,
      render: (_, record) => (
        <>
          <Typography.Text strong>{record.type ?? "-"}</Typography.Text>
          <br />
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {record.institutionStatus ?? "-"}
          </Typography.Text>
        </>
      ),
    },
    {
      title: "Aktifasi Lembaga",
      dataIndex: "isActive",
      width: 130,
      render: (val) => (
        <Tag color={val === true ? "green" : "red"}>
          {val === true ? "Aktif" : "Inaktif"}
        </Tag>
      ),
    },
    {
      title: "Provinsi & Kota Lembaga",
      dataIndex: "province",
      width: 220,
      render: (_, record) => (
        <>
          <Typography.Text strong>{record.province ?? "-"}</Typography.Text>
          <br />
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {record.district ?? "-"}
          </Typography.Text>
        </>
      ),
    },
    {
      title: "Status Legalitas",
      dataIndex: "legalityStatus",
      width: 150,
      render: (val) => <StatusTag status={val} />,
    },
    {
      title: "Status Fasilitas",
      dataIndex: "facilityStatus",
      width: 150,
      render: (val) => <StatusTag status={val} />,
    },
    {
      title: "Status Program",
      dataIndex: "programStatus",
      width: 150,
      render: (val) => <StatusTag status={val} />,
    },
    {
      title: "Update By",
      width: 150,
      render: (record) => <UpdatedByCell value={record} />,
    },
    {
      className: "last-cell-p",
      title: "Aksi",
      align: "right",
      width: 80,
      fixed: "right",
      render: (record) => (
        <Button
          icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
          color="default"
          variant="filled"
          onClick={() =>
            navigate(`/institutional-verification/detail/${record.uuid}`)
          }
        />
      ),
    },
  ];

  const filterFields = [
    {
      key: "type",
      label: "Jenis Lembaga",
      placeholder: "Semua Jenis Lembaga",
      searchable: true,
      options: institutionTypes?.data?.map((e) => ({
        label: e.name,
        value: e.uuid,
      })),
    },
    {
      key: "institutionForm",
      label: "Bentuk Lembaga",
      placeholder: "Semua Bentuk Lembaga",
      options: [
        { label: "Pelatihan", value: "Pelatihan" },
        {
          label: "Pendidikan dan Pelatihan",
          value: "Pendidikan dan Pelatihan",
        },
      ],
    },
    {
      key: "province",
      label: "Provinsi",
      placeholder: "Semua Provinsi",
      searchable: true,
      options: provinces?.map((prov) => ({
        label: prov.nama,
        value: prov.nama,
      })),
    },
    {
      key: "sectoral",
      label: "Sektor Pekerjaan",
      placeholder: "Semua Sektor Pekerjaan",
      searchable: true,
      options: sectoralOptions,
    },
    {
      key: "position",
      label: "Jabatan Pekerjaan",
      placeholder: "Semua Jabatan Pekerjaan",
      searchable: true,
      options: positionOptions,
    },
    {
      key: "legalityStatus",
      label: "Status Legalitas",
      placeholder: "Semua Status Legalitas",
      options: VERIFICATION_STATUS_OPTIONS,
    },
    {
      key: "facilityStatus",
      label: "Status Fasilitas",
      placeholder: "Semua Status Fasilitas",
      options: VERIFICATION_STATUS_OPTIONS,
    },
    {
      key: "programStatus",
      label: "Status Program",
      placeholder: "Semua Status Program",
      options: VERIFICATION_STATUS_OPTIONS,
    },
  ];

  const activeStatusCount = Object.values(filters).filter(Boolean).length;

  const statusFilterContent = (
    <div style={{ width: breakpoint.sm ? 460 : 280, padding: "4px 0" }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
        <Typography.Text strong style={{ fontSize: 14 }}>
          Filter Lembaga
        </Typography.Text>
        {activeStatusCount > 0 && (
          <Button
            type="link"
            size="small"
            danger
            style={{ padding: 0, height: "auto" }}
            onClick={() =>
              setFilters({
                type: null,
                institutionForm: null,
                province: null,
                sectoral: null,
                position: null,
                legalityStatus: null,
                facilityStatus: null,
                programStatus: null,
              })
            }
          >
            Reset
          </Button>
        )}
      </Flex>

      <Row gutter={[12, 12]}>
        {filterFields.map(
          ({ key, label, placeholder, options, searchable }) => (
            <Col xs={24} sm={12} key={key}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {label}
              </Typography.Text>
              <Select
                allowClear
                placeholder={placeholder}
                style={{ width: "100%", marginTop: 4 }}
                value={filters[key]}
                options={options}
                showSearch={searchable}
                optionFilterProp="label"
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, [key]: value ?? null }))
                }
              />
            </Col>
          ),
        )}
      </Row>
    </div>
  );

  return (
    <>
      {contextHolder}

      {/* Metrik Lembaga */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {metricsData.map(
          ({
            key,
            title,
            description,
            count,
            icon,
            backgroundColor,
            isPrimary,
          }) => (
            <Col xs={24} sm={12} md={6} key={key}>
              <Card
                style={{
                  backgroundColor,
                  color: isPrimary ? "#FFF" : "#000",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <div>{icon}</div>
                <div>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: 600,
                      marginTop: "5px",
                    }}
                  >
                    {count}
                  </div>
                  <div
                    style={{
                      fontWeight: 600,
                      marginTop: "12px",
                      fontSize: "16px",
                    }}
                  >
                    {title}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      marginTop: "4px",
                      fontStyle: "italic",
                      color: isPrimary ? "#eeeeee" : "#A9A9A9",
                    }}
                  >
                    {description}
                  </div>
                </div>
              </Card>
            </Col>
          ),
        )}
      </Row>

      {/* Tabel Data */}
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
      >
        {/* Input Search, Filter (popover), dan Export Excel */}
        <Flex
          justify="space-between"
          align="center"
          gap={12}
          wrap="wrap"
          style={{ paddingInline: 24, marginBottom: 16 }}
        >
          <Input
            style={{ width: breakpoint.md ? 260 : "100%", minWidth: 200 }}
            prefix={<Icon icon="hugeicons:search-01" width={16} height={16} />}
            placeholder="Cari..."
            onChange={(e) => setKeyword(e.target.value)}
            value={keyword}
            allowClear
          />

          <Flex gap={12} align="center">
            <Popover
              content={statusFilterContent}
              trigger="click"
              placement="bottomRight"
            >
              <Badge count={activeStatusCount} size="small" offset={[-2, 2]}>
                <Button
                  icon={
                    <Icon
                      icon="solar:filter-bold-duotone"
                      width={18}
                      height={18}
                    />
                  }
                  type={activeStatusCount > 0 ? "primary" : "default"}
                  ghost={activeStatusCount > 0}
                  style={{
                    width: breakpoint.sm ? "auto" : "100%",
                  }}
                >
                  Filter
                </Button>
              </Badge>
            </Popover>

            <Button
              type="primary"
              icon={
                <Icon icon="solar:export-bold-duotone" width={18} height={18} />
              }
              loading={exportLoading}
              onClick={handleExportExcel}
              style={{
                width: breakpoint.sm ? "auto" : "100%",
              }}
            >
              Export Excel
            </Button>
          </Flex>
        </Flex>

        <Table
          columns={columns}
          pagination={false}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={"id"}
          scroll={{ y: tableHeight, x: 1000 }}
          dataSource={instituteData?.data}
        />
        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={instituteData?.pagination.total}
          showSizeChanger={{
            showSearch: false,
          }}
          pageSizeOptions={["10", "20", "50"]}
          current={instituteData?.pagination.page}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>
    </>
  );
}
